import { getScrollBehavior } from 'core/utils/motion';
import { debounce } from 'lodash';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';
import type { SelectCheckboxOption } from './SelectCheckboxs';

type PickyRenderItemProps = {
  index: number;
  item: SelectCheckboxOption;
  isSelected: boolean;
  selectValue: (item: SelectCheckboxOption) => void;
  labelKey?: string;
  valueKey?: string;
  isMultiple?: boolean;
  disabled?: boolean;
};

type PickyRenderListProps = {
  getIsSelected: (item: SelectCheckboxOption) => boolean;
  items: SelectCheckboxOption[];
  multiple?: boolean;
  selectValue: (item: SelectCheckboxOption) => void;
};

type PickyRenderSelectAllProps = {
  filtered: boolean;
  tabIndex: number;
  allSelected: 'all' | 'none' | 'partial';
  toggleSelectAll: () => void;
  multiple: boolean;
};

export type { PickyRenderItemProps, PickyRenderListProps, PickyRenderSelectAllProps };

type UseSelectCheckboxs = {
  allItemsSelected: boolean;
  containerRef: React.RefObject<HTMLDivElement>;
  filterLabel: string;
  focusedOptionIndex: number;
  handleClose: () => void;
  handleContainerBlur: (event: React.FocusEvent<HTMLDivElement>) => void;
  handleContainerKeyDown: (event: React.KeyboardEvent<HTMLDivElement>) => void;
  handleOpen: () => void;
  handleOptionKeyDown: (
    event: React.KeyboardEvent,
    item: SelectCheckboxOption,
    selectValue: (item: SelectCheckboxOption) => void
  ) => void;
  hasValue: boolean;
  isDropdownOpen: boolean;
  labelId: string;
  labelKey: string;
  onChangeState: (data: SelectCheckboxOption | SelectCheckboxOption[]) => void;
  optionIndexMap: Map<unknown, number>;
  optionRefs: React.MutableRefObject<(HTMLDivElement | null)[]>;
  panelId: string;
  pickyId: string;
  setFocusedOptionIndex: React.Dispatch<React.SetStateAction<number>>;
  t: TFunction;
  triggerId: string;
  triggerProps: {
    'aria-controls': string;
    'aria-expanded': boolean;
    'aria-haspopup': 'listbox';
    'aria-labelledby'?: string;
  };
  valueKey: string;
  valueState: SelectCheckboxOption[];
};

type Params = {
  disabled: boolean;
  filterPlaceholder?: string;
  isError?: boolean;
  label?: React.ReactNode;
  labelKey: string;
  onChange: (value: SelectCheckboxOption[]) => void;
  options: SelectCheckboxOption[];
  shouldDebounce: boolean;
  value: SelectCheckboxOption[];
  valueKey: string;
};

function useSelectCheckboxs({
  disabled,
  filterPlaceholder,
  isError,
  label,
  labelKey,
  onChange,
  options,
  shouldDebounce,
  value,
  valueKey,
}: Params): UseSelectCheckboxs {
  const { t } = useTranslation(['common', 'placeholder']);

  const [valueState, setValueState] = useState<SelectCheckboxOption[]>(value);
  const [focusedOptionIndex, setFocusedOptionIndex] = useState<number>(-1);
  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const optionRefs = useRef<(HTMLDivElement | null)[]>([]);
  const onChangeRef = useRef(onChange);
  // Picky usa `id="picky"` por defecto: con dos selectores en la página se repetía.
  const [pickyId] = useState<string>(() => `select-checkboxs-${uuidv4()}`);
  // Picky deriva de su `id` el del panel (`-list`) y el del botón, al que añade `__button`
  // dos veces (una en `Picky` y otra en su `Button`): comprobado en react-picky 5.3.2.
  const triggerId = `${pickyId}__button__button`;
  const panelId = `${pickyId}-list`;
  const labelId = `${pickyId}-label`;
  const filterLabel = filterPlaceholder ?? t('placeholder:Search');

  useEffect(() => {
    onChangeRef.current = onChange;
  });

  useEffect(() => {
    setValueState(value);
  }, [value]);

  useEffect(() => {
    if (!containerRef.current) return;
    const trigger = containerRef.current.querySelector('.picky__input');
    if (trigger) {
      trigger.setAttribute('aria-invalid', String(Boolean(isError)));
    }
  }, [isError, value, options, disabled]);

  useEffect(() => {
    if (!containerRef.current) return;
    const combobox = containerRef.current.querySelector<HTMLElement>('[role="combobox"]');
    if (!combobox) return;

    function applyAriaFixes(): void {
      if (!combobox) return;
      const expected = String(isDropdownOpen);
      if (combobox.getAttribute('aria-expanded') !== expected) {
        combobox.setAttribute('aria-expanded', expected);
      }
      // Picky apunta `aria-controls` a su propio botón; el elemento controlado es el panel.
      if (combobox.getAttribute('aria-controls') !== panelId) {
        combobox.setAttribute('aria-controls', panelId);
      }
      // Picky pone `aria-haspopup={open}`: «true» equivale a `menu`, y el panel es un listbox.
      if (combobox.getAttribute('aria-haspopup') !== 'listbox') {
        combobox.setAttribute('aria-haspopup', 'listbox');
      }
      if (combobox.getAttribute('aria-owns')?.trim() === '') {
        combobox.removeAttribute('aria-owns');
      }
    }

    applyAriaFixes();

    const observer = new MutationObserver(applyAriaFixes);
    observer.observe(combobox, {
      attributes: true,
      attributeFilter: ['aria-expanded', 'aria-controls', 'aria-haspopup', 'aria-owns'],
    });

    return () => observer.disconnect();
  }, [isDropdownOpen, panelId]);

  useEffect(() => {
    if (!containerRef.current) return;
    const combobox = containerRef.current.querySelector<HTMLElement>('[role="combobox"]');
    if (!combobox) return;

    if (label) {
      combobox.setAttribute('aria-labelledby', labelId);
    } else {
      combobox.removeAttribute('aria-labelledby');
    }
  }, [label, labelId]);

  // El `aria-label` del filtro de Picky es el literal «filter options» y no admite prop.
  // React no lo reescribe mientras ese literal no cambie; sin dependencias, también cubre
  // un filtro que se monte después (`hasFilter`). Sin listeners, no hay nada que limpiar.
  useEffect(() => {
    const filterInput = containerRef.current?.querySelector<HTMLInputElement>('.picky__filter__input');
    if (filterInput && filterInput.getAttribute('aria-label') !== filterLabel) {
      filterInput.setAttribute('aria-label', filterLabel);
    }
  });

  useEffect(() => {
    if (!isDropdownOpen) {
      setFocusedOptionIndex(-1);
    }
  }, [isDropdownOpen]);

  useEffect(() => {
    if (focusedOptionIndex >= 0 && optionRefs.current[focusedOptionIndex]) {
      optionRefs.current[focusedOptionIndex]?.scrollIntoView({
        block: 'nearest',
        behavior: getScrollBehavior(),
      });
    }
  }, [focusedOptionIndex]);

  const debounceCheck = useCallback(
    debounce((newList: SelectCheckboxOption[]) => {
      onChangeRef.current(newList);
    }, 100),
    []
  );

  useEffect(() => {
    return () => {
      debounceCheck.cancel();
    };
  }, [debounceCheck]);

  useEffect(() => {
    if (shouldDebounce) {
      debounceCheck(valueState);
    } else {
      onChangeRef.current(valueState);
    }
  }, [valueState, shouldDebounce, debounceCheck]);

  const optionIndexMap = useMemo(() => {
    const map = new Map<unknown, number>();
    options.forEach((opt, i) => {
      map.set(opt[valueKey], i);
    });
    return map;
  }, [options, valueKey]);

  function handleClose(): void {
    setIsDropdownOpen(false);
  }

  function handleOpen(): void {
    setIsDropdownOpen(true);
    const firstEnabledIndex = options.findIndex((opt) => !opt.disabled);
    setFocusedOptionIndex(firstEnabledIndex);
  }

  function getTrigger(): HTMLButtonElement | null {
    return containerRef.current?.querySelector<HTMLButtonElement>('.picky__input') ?? null;
  }

  // Picky no tiene `open` controlado ni cierra por teclado: el único cierre que expone es
  // su propio botón, que alterna el panel.
  function closeDropdown(): void {
    getTrigger()?.click();
  }

  // En burbuja y con `stopPropagation`: un `Dialog` padre (MUI cierra con Escape sin mirar
  // `defaultPrevented`) o un `Popover` padre (pila de Escape en `document`) no se cierran
  // mientras el panel esté abierto. Picky no usa portal, así que el evento pasa por aquí.
  function handleContainerKeyDown(event: React.KeyboardEvent<HTMLDivElement>): void {
    if (event.key !== 'Escape' || !isDropdownOpen) return;
    event.stopPropagation();
    closeDropdown();
    getTrigger()?.focus();
  }

  // Solo si el foco va a otro elemento (Tab, Shift+Tab): sin `relatedTarget` el foco cae
  // al `body` también al pulsar una zona no enfocable del propio panel, y ese clic ya lo
  // resuelve el click-fuera de Picky.
  function handleContainerBlur(event: React.FocusEvent<HTMLDivElement>): void {
    const nextFocused = event.relatedTarget;
    if (!isDropdownOpen || !(nextFocused instanceof Node)) return;
    if (containerRef.current?.contains(nextFocused)) return;
    closeDropdown();
  }

  function onChangeState(data: SelectCheckboxOption | SelectCheckboxOption[]): void {
    const dataArray = Array.isArray(data) ? data : [data];
    const disabledOptions = options.filter((opt) => opt.disabled);
    const currentDisabledSelected = disabledOptions.filter(
      (disabledOpt) =>
        Array.isArray(valueState) &&
        valueState.some((selectedItem) => selectedItem[valueKey] === disabledOpt[valueKey])
    );
    const newDataWithoutDisabled = dataArray.filter((item) => {
      const optionItem = options.find((opt) => opt[valueKey] === item[valueKey]);
      return !optionItem?.disabled;
    });
    setValueState([...currentDisabledSelected, ...newDataWithoutDisabled]);
  }

  // Sin un elemento montado no hay a dónde llevar el foco: con el filtro activo, Picky solo pinta
  // las opciones que coinciden, pero los índices son los de `options`.
  function isNavigable(index: number): boolean {
    return !options[index]?.disabled && Boolean(optionRefs.current[index]);
  }

  // El foco se mueve aquí y no en el efecto de `focusedOptionIndex`, porque `handleOpen` también
  // fija el índice y el efecto se lo quitaría al filtro. `preventScroll`: el desplazamiento lo
  // hace el efecto, que respeta `prefers-reduced-motion`.
  function moveFocusTo(index: number): void {
    setFocusedOptionIndex(index);
    optionRefs.current[index]?.focus({ preventScroll: true });
  }

  function handleOptionKeyDown(
    event: React.KeyboardEvent,
    item: SelectCheckboxOption,
    selectValue: (item: SelectCheckboxOption) => void
  ): void {
    const key = event.key;

    if (key === 'Enter' || key === ' ') {
      event.preventDefault();
      event.stopPropagation();
      if (!item.disabled) {
        selectValue(item);
      }
      return;
    }

    if (key === 'ArrowDown') {
      event.preventDefault();
      let nextIndex = focusedOptionIndex + 1;
      while (nextIndex < options.length && !isNavigable(nextIndex)) {
        nextIndex++;
      }
      if (nextIndex < options.length) {
        moveFocusTo(nextIndex);
      }
      return;
    }

    if (key === 'ArrowUp') {
      event.preventDefault();
      let prevIndex = focusedOptionIndex - 1;
      while (prevIndex >= 0 && !isNavigable(prevIndex)) {
        prevIndex--;
      }
      if (prevIndex >= 0) {
        moveFocusTo(prevIndex);
      }
    }
  }

  // Con `label`, el foco en el disparador anuncia la etiqueta y después su texto («3 seleccionados»).
  const triggerProps: UseSelectCheckboxs['triggerProps'] = {
    'aria-controls': panelId,
    'aria-expanded': isDropdownOpen,
    'aria-haspopup': 'listbox',
    ...(label ? { 'aria-labelledby': `${labelId} ${triggerId}` } : {}),
  };

  const hasValue = Array.isArray(valueState) && valueState.length > 0;
  const allItemsSelected = hasValue && options.length === valueState.length;

  return {
    containerRef,
    filterLabel,
    focusedOptionIndex,
    handleClose,
    handleContainerBlur,
    handleContainerKeyDown,
    handleOpen,
    handleOptionKeyDown,
    hasValue,
    allItemsSelected,
    isDropdownOpen,
    labelId,
    labelKey,
    onChangeState,
    optionIndexMap,
    optionRefs,
    panelId,
    pickyId,
    setFocusedOptionIndex,
    t,
    triggerId,
    triggerProps,
    valueKey,
    valueState,
  };
}

export default useSelectCheckboxs;
